import { useEffect } from "react";

const SITE = "Ayrshire Hub";
const CANONICAL_BASE = "https://www.ayrshirehub.co.uk";
const DEFAULT_DESCRIPTION =
  "Ayrshire's digital community — local news, tradesperson jobs, events, deals and a business directory for Ayr, Kilmarnock, Irvine and across Ayrshire, Scotland.";
const LD_SCRIPT_ID = "json-ld-main";

export interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  type?: "website" | "article";
  noIndex?: boolean;
  jsonLd?: Record<string, unknown>;
}

export function useSEO({
  title,
  description,
  image,
  type = "website",
  noIndex = false,
  jsonLd,
}: SEOProps = {}) {
  const fullTitle = title
    ? `${title} | ${SITE}`
    : `${SITE} — The Digital Town Square for Ayrshire`;
  const fullDescription = description ?? DEFAULT_DESCRIPTION;
  const ogImage =
    image ?? `${CANONICAL_BASE}/opengraph.jpg`;
  // Always use the canonical domain — prevents replit.app/replit.dev leaking into canonical
  const canonicalUrl =
    typeof window !== "undefined"
      ? `${CANONICAL_BASE}${window.location.pathname}`
      : CANONICAL_BASE;

  useEffect(() => {
    document.title = fullTitle;

    const setMeta = (key: string, value: string, attr = "name") => {
      let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.content = value;
    };

    setMeta("description", fullDescription);
    setMeta("robots", noIndex ? "noindex,nofollow" : "index,follow");
    setMeta("keywords", "Ayrshire, Ayr, Kilmarnock, Irvine, community, local news, events, deals, jobs, businesses");

    setMeta("og:type", type, "property");
    setMeta("og:site_name", SITE, "property");
    setMeta("og:title", fullTitle, "property");
    setMeta("og:description", fullDescription, "property");
    setMeta("og:image", ogImage, "property");
    setMeta("og:url", canonicalUrl, "property");

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", fullDescription);
    setMeta("twitter:image", ogImage);

    let link = document.querySelector<HTMLLinkElement>("link[rel='canonical']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = canonicalUrl;

    let script = document.getElementById(LD_SCRIPT_ID);
    if (jsonLd) {
      if (!script) {
        script = document.createElement("script");
        script.id = LD_SCRIPT_ID;
        script.setAttribute("type", "application/ld+json");
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }
  }, [fullTitle, fullDescription, ogImage, type, canonicalUrl, noIndex, jsonLd]);
}
